import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { vatLabel } from "@/lib/api";
import { toast } from "sonner";
import { Hand, X, ArrowLeft } from "lucide-react";

/**
 * Two-step "Χειροκίνητα" flow:
 *   Step 1 – grid of all cash-register departments. Tapping a card
 *            advances to step 2.
 *   Step 2 – numeric input asking for the unit price.
 *
 * On submit the parent receives an *ad-hoc cart line* (no Product is
 * persisted). The line carries the department's VAT class so the FTEXT
 * receipt is fiscally correct.
 *
 *   onSubmit({ description, price, vat_rate, department })
 */
export default function ManualDepartmentDialog({
  open,
  onOpenChange,
  departments,
  onSubmit,
}) {
  const [step, setStep] = useState("pick"); // "pick" | "price"
  const [dept, setDept] = useState(null);
  const [price, setPrice] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("pick");
    setDept(null);
    setPrice("");
  }, [open]);

  const handlePick = (d) => {
    setDept(d);
    setPrice("");
    setStep("price");
  };

  const handleAccept = () => {
    const p = parseFloat(String(price).replace(",", "."));
    if (!p || p <= 0) {
      toast.error("Δώσε τιμή > 0");
      return;
    }
    onSubmit?.({
      description: `Τμήμα #${dept.number} — ${dept.description}`,
      price: p,
      vat_rate: Number(dept.vat_number) || 3,
      department: Number(dept.number),
    });
    onOpenChange(false);
  };

  // Numpad key press — appends digits, single decimal separator, cap at
  // 2 fractional digits. The display normalises commas to dots for
  // parseFloat correctness later.
  const pressKey = (k) => {
    setPrice((cur) => {
      const s = String(cur || "");
      if (k === "." || k === ",") {
        if (s.includes(".")) return s;
        return s === "" ? "0." : s + ".";
      }
      // digit
      if (s.includes(".")) {
        const [whole, frac = ""] = s.split(".");
        if (frac.length >= 2) return s; // cap 2 decimals
        return `${whole}.${frac}${k}`;
      }
      // strip leading zeros (keep single 0)
      if (s === "0") return k;
      return s + k;
    });
  };
  const pressBackspace = () => setPrice((s) => String(s).slice(0, -1));
  const pressClear = () => setPrice("");

  // Format the value for the display: always show 2 decimals when a
  // separator has been entered; otherwise the raw integer string.
  const displayPrice = (() => {
    const s = String(price || "");
    if (s === "") return "0,00";
    if (!s.includes(".")) return `${s},00`;
    const [whole, frac = ""] = s.split(".");
    return `${whole || "0"},${(frac + "00").slice(0, 2)}`;
  })();

  // Department palette — assign a rotating colour so tiles are easy to
  // tell apart on a touch screen. Department.color is not in the schema
  // so we pick from a fixed list.
  const colors = useMemo(
    () => [
      "bg-emerald-500",
      "bg-indigo-500",
      "bg-amber-500",
      "bg-pink-500",
      "bg-cyan-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-teal-500",
    ],
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-3xl p-0 gap-0 overflow-hidden"
        data-testid="manual-dept-dialog"
      >
        {/* Header */}
        <div className="px-6 py-4 bg-indigo-50 border-b-2 border-indigo-200 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-indigo-100 grid place-items-center shrink-0">
            <Hand size={22} className="text-indigo-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-lg font-bold text-indigo-800">
              {step === "pick" ? "ΕΠΙΛΟΓΗ ΤΜΗΜΑΤΟΣ" : "ΤΙΜΗ ΜΟΝΑΔΑΣ"}
            </div>
            <div className="text-[11px] text-slate-600">
              {step === "pick"
                ? "Επιλέξτε το τμήμα της ταμειακής για χειροκίνητη πώληση"
                : `Τμήμα #${dept?.number} — ${dept?.description} (${vatLabel(dept?.vat_number)})`}
            </div>
          </div>
          {step === "price" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setStep("pick")}
              data-testid="manual-dept-back"
            >
              <ArrowLeft size={14} className="mr-1" />
              Πίσω
            </Button>
          )}
        </div>

        {/* Body */}
        {step === "pick" ? (
          <div className="p-5 bg-white max-h-[65vh] overflow-y-auto">
            {departments.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-10">
                Δεν υπάρχουν τμήματα. Ορίστε πρώτα τμήματα στις Ρυθμίσεις.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {departments.map((d, i) => (
                  <button
                    key={d.number}
                    type="button"
                    onClick={() => handlePick(d)}
                    className={`${colors[i % colors.length]} text-white rounded-lg p-4 text-left hover:brightness-110 active:scale-[0.98] transition-all shadow-sm`}
                    data-testid={`manual-dept-tile-${d.number}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">
                      Τμήμα #{d.number}
                    </div>
                    <div className="font-bold text-base leading-tight mt-1 line-clamp-2">
                      {d.description}
                    </div>
                    <div className="text-[10px] opacity-80 mt-1">
                      {vatLabel(d.vat_number)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 bg-white grid gap-4">
            {/* Giant price display */}
            <div className="rounded-xl bg-slate-900 text-emerald-300 px-6 py-5 shadow-inner">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-1">
                Τιμή Μονάδας
              </div>
              <div
                className="font-mono font-black text-7xl leading-none text-right tabular-nums"
                data-testid="manual-dept-price-display"
              >
                {displayPrice}<span className="text-emerald-500 ml-2">€</span>
              </div>
              <div className="text-[11px] text-slate-400 mt-2 text-right">
                ΦΠΑ: <span className="font-bold text-slate-200">{vatLabel(dept?.vat_number)}</span>
              </div>
            </div>

            {/* Inline touch numpad — auto-shown, no input field needed */}
            <div className="grid grid-cols-3 gap-2.5">
              {["7", "8", "9", "4", "5", "6", "1", "2", "3"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => pressKey(k)}
                  className="h-16 rounded-lg bg-white border-2 border-slate-200 text-3xl font-bold hover:bg-slate-50 active:scale-95 transition-transform shadow-sm"
                  data-testid={`manual-dept-key-${k}`}
                >
                  {k}
                </button>
              ))}
              <button
                type="button"
                onClick={() => pressKey(".")}
                className="h-16 rounded-lg bg-white border-2 border-slate-200 text-3xl font-bold hover:bg-slate-50 active:scale-95 transition-transform shadow-sm"
                data-testid="manual-dept-key-dot"
              >
                ,
              </button>
              <button
                type="button"
                onClick={() => pressKey("0")}
                className="h-16 rounded-lg bg-white border-2 border-slate-200 text-3xl font-bold hover:bg-slate-50 active:scale-95 transition-transform shadow-sm"
                data-testid="manual-dept-key-0"
              >
                0
              </button>
              <button
                type="button"
                onClick={pressBackspace}
                className="h-16 rounded-lg bg-amber-100 border-2 border-amber-300 text-amber-800 text-2xl font-bold hover:bg-amber-200 active:scale-95 transition-transform shadow-sm"
                data-testid="manual-dept-key-bksp"
                aria-label="Backspace"
              >
                ⌫
              </button>
              <button
                type="button"
                onClick={pressClear}
                className="col-span-3 h-12 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 text-sm font-bold hover:bg-red-100 active:scale-95 transition-transform"
                data-testid="manual-dept-key-clear"
              >
                C — Καθαρισμός
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-4 border-t-2 border-slate-200 bg-slate-50 flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-12 px-6 text-sm font-bold"
            data-testid="manual-dept-cancel"
          >
            <X size={16} className="mr-2" />
            ΑΚΥΡΩΣΗ
          </Button>
          {step === "price" && (
            <Button
              type="button"
              onClick={handleAccept}
              className="h-12 px-6 text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="manual-dept-accept"
            >
              ΠΡΟΣΘΗΚΗ ΣΤΗΝ ΠΩΛΗΣΗ
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
